import { Button } from "@/Button";
import { LayoutHeader } from "@/Header";
import { Dialog } from "@/Dialog";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useInquiryCreate } from "~/services/customer/inquiry";

interface InfoSuggestParams {
  companySeq: string;
  companyName: string;
}

export const Route = createFileRoute("/customer/layout/info-suggest")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): InfoSuggestParams => {
    return {
      companySeq: search.companySeq as string,
      companyName: search.companyName as string,
    };
  },
});

// 세부 유형 — 하나 선택
const SUB_TYPES = ["전화번호", "영업시간", "주소", "폐업/이전", "기타"];

function RouteComponent() {
  const { history } = useRouter();
  const { companySeq, companyName } = Route.useSearch();

  const [subType, setSubType] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [contact, setContact] = useState<string>("");

  const [isSuccessDialogVisible, setIsSuccessDialogVisible] = useState(false);
  const [isErrorDialogVisible, setIsErrorDialogVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { mutate: createInquiry, isPending } = useInquiryCreate();

  const isValid = subType.trim().length > 0 && content.trim().length > 0;

  const handleSubmit = () => {
    if (!isValid || isPending) return;

    createInquiry(
      {
        category: "STORE_INFO",
        subType,
        companySeq: companySeq ? parseInt(companySeq) : undefined,
        companyName,
        content: content.trim(),
        contact: contact.trim() || undefined,
      },
      {
        onSuccess: () => setIsSuccessDialogVisible(true),
        onError: () => {
          setErrorMessage("접수에 실패했습니다. 잠시 후 다시 시도해 주세요.");
          setIsErrorDialogVisible(true);
        },
      },
    );
  };

  const handleSuccessDialogClose = () => {
    setIsSuccessDialogVisible(false);
    history.back();
  };

  return (
    <>
      <LayoutHeader
        title={"정보 수정 및 제안"}
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
        <div className="flex-auto px-5 pt-5 pb-12 bg-[#F5F5F5]">
          {/* 대상 매장 */}
          <div className="mb-6">
            <div className="font-medium text-gray-500 text-sm">대상 매장</div>
            <div className="mt-1 text-lg font-semibold">{companyName}</div>
          </div>

          {/* 어떤 정보가 다른가요 */}
          <div className="mb-6">
            <div className="mb-2 text-sm font-semibold text-gray-700">
              어떤 정보가 다른가요?
            </div>
            <div className="flex flex-wrap gap-2">
              {SUB_TYPES.map((t) => {
                const active = subType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setSubType(t)}
                    className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
                      active
                        ? "bg-yellow/10 text-yellow border-yellow font-semibold"
                        : "bg-white text-gray-600 border-gray-200"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 정확한 정보 */}
          <div className="mb-6">
            <div className="mb-2 text-sm font-semibold text-gray-700">
              정확한 정보를 알려주세요
            </div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              maxLength={2000}
              placeholder="예) 전화번호가 02-333-7799로 바뀌었어요."
              className="w-full p-3 text-base bg-white border border-gray-200 rounded-lg resize-none focus:outline-yellow"
            />
          </div>

          {/* 연락처 (선택) */}
          <div className="mb-2">
            <div className="mb-2 text-sm font-semibold text-gray-700">
              연락처 <span className="font-normal text-gray-400">(선택)</span>
            </div>
            <input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              inputMode="tel"
              maxLength={50}
              placeholder="확인이 필요할 때만 연락드려요"
              className="w-full p-3 text-base bg-white border border-gray-200 rounded-lg focus:outline-yellow"
            />
          </div>
          <p className="text-xs leading-relaxed text-gray-400">
            제안 내용은 관리자 확인 후 반영돼요. 감사합니다 🙏
          </p>
        </div>

        <div className="sticky bottom-14 w-full px-5 py-3 bg-[#F5F5F5]">
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            <div
              className={`py-3 mx-auto w-full text-base font-semibold text-center text-white rounded-full ${
                !isValid || isPending ? "bg-gray-300" : "bg-yellow"
              }`}
            >
              제안 보내기
            </div>
          </Button>
        </div>
      </div>

      {/* 성공 Dialog */}
      <Dialog
        visible={isSuccessDialogVisible}
        onClose={handleSuccessDialogClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="mb-2 text-lg font-semibold text-center">
            제안이 접수되었어요
          </div>
          <div className="mb-2 text-sm text-center text-[#666666]">
            소중한 의견 감사합니다. 확인 후 반영하겠습니다.
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white rounded-full bg-yellow"
              onClick={handleSuccessDialogClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 에러 Dialog */}
      <Dialog
        visible={isErrorDialogVisible}
        onClose={() => setIsErrorDialogVisible(false)}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="mb-2 text-lg font-semibold text-center">
            접수 실패
          </div>
          <div className="mb-2 text-sm text-center text-[#666666]">
            {errorMessage}
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white rounded-full bg-yellow"
              onClick={() => setIsErrorDialogVisible(false)}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
